import { useRef, useState } from 'react';

import { parseAmountToMinor } from '../../shared/amount.js';
import { MAX_PARTICIPANTS, MAX_TITLE_LENGTH, MIN_PARTICIPANTS } from '../../shared/constants.js';
import { CURRENCIES, DEFAULT_CURRENCY, type CurrencyCode } from '../../shared/currencies.js';
import { useStrings } from '../i18n.js';
import { AmountInput } from '../components/AmountInput.js';
import { Field } from '../components/Field.js';
import { ApiFailure, createRoom } from '../lib/api.js';
import { track } from '../lib/analytics.js';
import { buildManageFragment } from '../lib/fragment.js';
import {
  generateParticipantToken,
  hasVisitedRoomAsRecipient,
  markRoomCreated,
  rememberParticipantToken,
  roomsCreatedCount,
} from '../lib/storage.js';
import { Turnstile, type TurnstileHandle } from './Turnstile.js';

/**
 * The creation form.
 *
 * One card, four fields, one button — no wizard, no step counter, no account. The
 * creator's own amount is collected here, which is why a new room starts at 1 of N.
 *
 * Validation happens in the browser for immediate feedback and again on the server for
 * real; a rejected submission never clears what was typed.
 */

export function CreateForm() {
  const s = useStrings();

  const [title, setTitle] = useState('');
  const [people, setPeople] = useState('5');
  const [currency, setCurrency] = useState<CurrencyCode>(DEFAULT_CURRENCY);
  const [amount, setAmount] = useState('');

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const startedRef = useRef(false);
  const turnstile = useRef<TurnstileHandle>(null);

  /** First interaction with the form is the top of the creation funnel. */
  function noteStart() {
    if (startedRef.current) return;
    startedRef.current = true;
    track('create_start', 'public', hasVisitedRoomAsRecipient() ? 'prior_recipient' : '-');
    if (hasVisitedRoomAsRecipient()) track('participant_to_creator', 'public');
  }

  function validate(): { title: string; expectedCount: number; amountMinor: number } | null {
    const next: Record<string, string | undefined> = {};

    const trimmedTitle = title.trim();
    if (trimmedTitle === '') next.title = s.createForm.errorTitleRequired;
    else if (trimmedTitle.length > MAX_TITLE_LENGTH) next.title = s.createForm.errorTitleTooLong;

    const expectedCount = Number(people);
    if (
      !Number.isInteger(expectedCount) ||
      expectedCount < MIN_PARTICIPANTS ||
      expectedCount > MAX_PARTICIPANTS
    ) {
      next.people = s.createForm.errorPeopleRange;
    }

    let amountMinor = 0;
    if (amount.trim() === '') {
      next.amount = s.createForm.errorAmountRequired;
    } else {
      const parsed = parseAmountToMinor(amount, currency);
      if (!parsed.ok) {
        next.amount =
          parsed.error === 'too_precise'
            ? s.createForm.errorAmountTooPrecise
            : parsed.error === 'too_large'
              ? s.createForm.errorAmountTooLarge
              : s.createForm.errorAmountInvalid;
      } else {
        amountMinor = parsed.minor;
      }
    }

    setErrors(next);
    if (Object.values(next).some(Boolean)) return null;
    return { title: trimmedTitle, expectedCount, amountMinor };
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (isSubmitting) return;

    noteStart();
    setFormError(undefined);

    const valid = validate();
    if (!valid) return;

    setIsSubmitting(true);

    try {
      const participantToken = generateParticipantToken();
      const turnstileToken = await turnstile.current?.getToken();

      const result = await createRoom({
        title: valid.title,
        currency,
        expectedCount: valid.expectedCount,
        amountMinor: valid.amountMinor,
        participantToken,
        ...(turnstileToken ? { turnstileToken } : {}),
      });

      rememberParticipantToken(result.room.id, participantToken);
      const isRepeat = roomsCreatedCount() > 0;
      markRoomCreated(result.room.id);

      track('room_created', 'public', isRepeat ? 'repeat_room' : 'first_room');
      if (isRepeat) track('second_room_create', 'public');

      /*
       * Go straight to the room, with the manage secret in the fragment.
       *
       * The fragment is never sent to the server and never appears in a Referer header,
       * so the organiser's credential travels only inside their own browser. `new=1`
       * makes the share screen lead once; it is dropped on continue.
       */
      window.location.assign(
        `/r/${result.room.id}${buildManageFragment(result.adminSecret, true)}`,
      );
    } catch (caught) {
      track('error_shown', 'public');
      turnstile.current?.reset();

      if (caught instanceof ApiFailure) {
        if (caught.code === 'rate_limited') setFormError(s.createForm.errorRateLimited);
        else if (caught.code === 'turnstile_failed') setFormError(s.createForm.errorTurnstile);
        else if (caught.field)
          setErrors((prev) => ({ ...prev, [fieldKey(caught.field!)]: caught.message }));
        else setFormError(caught.message || s.createForm.errorGeneric);
      } else {
        setFormError(s.createForm.errorGeneric);
      }
      setIsSubmitting(false);
    }
  }

  return (
    <form className="card stack-lg" onSubmit={handleSubmit} onFocus={noteStart} noValidate>
      <h2 className="visually-hidden">{s.createForm.legend}</h2>

      <Field label={s.createForm.titleLabel} hint={s.createForm.titleHint} error={errors.title}>
        {(props) => (
          <input
            {...props}
            className="input"
            type="text"
            name="title"
            autoComplete="off"
            maxLength={MAX_TITLE_LENGTH}
            placeholder={s.createForm.titlePlaceholder}
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          />
        )}
      </Field>

      <div className="field-row field-row--two">
        <Field
          label={s.createForm.peopleLabel}
          hint={s.createForm.peopleHint}
          error={errors.people}
        >
          {(props) => (
            <input
              {...props}
              className="input"
              type="number"
              name="people"
              inputMode="numeric"
              min={MIN_PARTICIPANTS}
              max={MAX_PARTICIPANTS}
              step={1}
              value={people}
              onChange={(event) => {
                setPeople(event.target.value);
              }}
            />
          )}
        </Field>

        <Field label={s.createForm.currencyLabel}>
          {(props) => (
            <select
              {...props}
              className="select"
              name="currency"
              value={currency}
              onChange={(event) => {
                setCurrency(event.target.value as CurrencyCode);
              }}
            >
              {CURRENCIES.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.code} · {option.label}
                </option>
              ))}
            </select>
          )}
        </Field>
      </div>

      {/*
        The submit button sits beside the amount field from 30rem up, as the wireframe
        shows. It is not only closer to the reference: it is what keeps the entire
        creation action above the fold on a laptop.
      */}
      <AmountInput
        label={s.createForm.amountLabel}
        hint={s.createForm.amountHint}
        currency={currency}
        value={amount}
        onChange={setAmount}
        error={errors.amount}
        trailing={
          <button
            type="submit"
            className="button button--primary amount-row__action"
            disabled={isSubmitting}
          >
            {isSubmitting ? s.createForm.submitting : s.createForm.submit}
          </button>
        }
      />

      <Turnstile ref={turnstile} />

      <p className="trust-line">{s.createForm.trustLine}</p>

      {formError ? (
        <p className="callout callout--alert" role="alert">
          {formError}
        </p>
      ) : null}
    </form>
  );
}

/** Map an API field name onto the local error key. */
function fieldKey(field: string): string {
  if (field === 'expectedCount') return 'people';
  return field;
}
